import {
  EditorMarkup,
  EditorMarkupError,
  EditorWord,
  SignCode,
  SignCodeWithDiscourseUuid,
  SignCodeWithUuid,
} from '@oare/types';
import { v4 } from 'uuid';
import { applyMarkup } from './applyMarkup';
import { getMarkupContextErrors, getMarkupInputErrors } from './markupErrors';

/**
 * The two server lookups the editor needs to turn typed text into signs.
 * The serverProxy satisfies it; tests pass a fake.
 */
export interface SignLookup {
  getFormattedSign: (sign: string) => Promise<string[]>;
  getSignCode: (sign: string, post: string) => Promise<SignCode>;
}

// Marks a postfix determinative in the text used for parsing. The markup
// parsers only know `%`, so they get a copy where postfix determinatives use
// `%` too; this copy tells the two kinds apart.
const POSTFIX = '¤';
// Marks a plain boundary between a sign and a prefix determinative written
// right after it (`KIŠIB(d)EN`): the sign before is not a determinative and
// the spelling puts no separator there. The markup copy uses `%`.
const BOUNDARY = '¦';

// A determinative is a postfix (`a-lim(ki)`) when a sign ends right before it
// and no sign follows it (opening damage brackets on the next sign do not
// count, so `KIŠIB(d)[EN]` has a following sign). Anything else (`(d)UTU`,
// `šu-(d)EN`, `[(d)]UTU`, `(TÚG)(ḪI)`, `KIŠIB(d)EN`) is a prefix. Damage brackets right around the parenthesis
// (`a-lim[(ki)]`, `a-lim⸢(ki)⸣`, `[(d)]UTU`) belong to the determinative.
const CANNOT_END_SIGN = /[\s\-.+%=()[⸢{«‹]/;
const STARTS_WITH_SIGN = /^[\p{L}\p{N}]/u;

// The markup to strip from a sign before looking it up: brackets, half
// brackets, «» ‹›, : ; * ? \ !, quoted original/alternate readings and a
// leading /. It is a pattern, not markupErrors.ts's MARKUP_CHARS list: that
// list also holds dividers (% +), determinative parentheses and x, which
// must stay in a sign here (x is the unread-sign reading).
export const MARKUP_CHARS_PATTERN =
  /([[\]{}⸢⸣«»‹›:;*?\\!])|(".+")|('.+')|(^\/)+/g;

/**
 * Rewrites `(X)` determinatives into the editor's divider notation:
 * a prefix becomes `X%` and a postfix becomes `%X`. `(.)` between chained
 * determinatives, as in `(TÚG)(.)(ḪI)(.)(A)`, becomes a plain `.`.
 * Returns the text for the markup parsers and the text for sign parsing.
 */
export const convertDeterminatives = (
  rowText: string
): { markupText: string; parseText: string } => {
  const text = rowText.replace(/\(\.\)/g, '.');
  const convert = (postfixDivider: string, boundary: string) =>
    text.replace(
      /([[⸢]*)\(([^)]+)\)([\]⸣]*)/g,
      (
        match: string,
        opening: string,
        inner: string,
        closing: string,
        offset: number,
        full: string
      ) => {
        const before = offset > 0 ? full[offset - 1] : '';
        const after = full.slice(offset + match.length);
        const signBefore = before !== '' && !CANNOT_END_SIGN.test(before);
        const isPostfix =
          signBefore && !STARTS_WITH_SIGN.test(after.replace(/^[[⸢]+/, ''));
        // Damage brackets around the parenthesis stay with the determinative
        // and the divider goes outside them, so the markup lands on the
        // determinative: `a-lim[(ki)]` becomes `a-lim%[ki]` and `[(d)]UTU`
        // becomes `[d]%UTU`.
        const determinative = `${opening}${inner}${closing}`;
        if (isPostfix) {
          return `${postfixDivider}${determinative}`;
        }
        // A prefix right after a sign also needs a boundary before it.
        return `${signBefore ? boundary : ''}${determinative}%`;
      }
    );
  return {
    markupText: convert('%', '%'),
    parseText: convert(POSTFIX, BOUNDARY),
  };
};

export interface ParsedSign {
  // The sign as typed, markup included.
  text: string;
  isDeterminative: boolean;
  // The dividers typed after the sign, including the internal `%`/`¤`.
  divider: string;
}

/**
 * Splits one word of `parseText` into its signs. A sign followed by `%` is a
 * prefix determinative; a sign preceded by `¤` is a postfix determinative.
 */
export const parseWord = (word: string): ParsedSign[] => {
  const parts = word.split(/([-.+%¤¦]+)/);
  const signs: ParsedSign[] = [];
  for (let i = 0; i < parts.length; i += 2) {
    const before = i > 0 ? parts[i - 1] : '';
    const after = parts[i + 1] || '';
    if (parts[i] !== '') {
      signs.push({
        text: parts[i],
        isDeterminative: after.includes('%') || before.includes(POSTFIX),
        divider: after,
      });
    }
  }
  return signs;
};

export interface SignPiece {
  code: SignCodeWithUuid;
  isDeterminative: boolean;
  divider: string;
}

const spellingSeparator = (divider: string) => divider.replace(/[%¤¦]/g, '');

/**
 * Builds a word's explicit spelling the way the corpus and dictionary write
 * it: `a-lim(ki)`, `a-lim(ki)-ma`, `(d)UTU`, `(TÚG)(.)(ḪI)(.)(A)ku-ta-nu`,
 * `50+LÁ+2`.
 */
export const buildSpelling = (pieces: SignPiece[]): string =>
  pieces
    .map(({ code, isDeterminative, divider }, idx) => {
      if (isDeterminative) {
        // A `.` between two determinatives is the chain separator `(.)`. A
        // prefix determinative otherwise takes no separator (it carries
        // `%`), and a postfix one keeps the divider typed after it
        // (`a-lim(ki)-ma`, `a-lim(ki).X`).
        const nextIsDeterminative = !!pieces[idx + 1]?.isDeterminative;
        let separator = spellingSeparator(divider);
        if (divider.includes('.') && nextIsDeterminative) {
          separator = '(.)';
        } else if (divider.includes('%') && !divider.includes('.')) {
          separator = '';
        }
        return `(${code.value || ''})${separator}`;
      }
      if (
        code.markup &&
        code.markup.markup.some(
          markup => markup.type === 'superfluous' || markup.type === 'erasure'
        )
      ) {
        return '';
      }
      if (code.type === 'undetermined') {
        if (code.reading === '@') {
          return '...';
        }
        if (code.reading && code.reading.includes('x')) {
          return `${code.reading}${spellingSeparator(divider)}`;
        }
        return '';
      }
      return `${code.value || ''}${spellingSeparator(divider)}`;
    })
    .join('');

/**
 * LÁ between two numbers in one word is a minus sign (`50+LÁ+2` is 48), and
 * the corpus stores it as a number, which also makes the word a number.
 */
export const markSubtractiveNumbers = (codes: SignCode[]): SignCode[] =>
  codes.map((code, idx) => {
    const isLa = code.value === 'LÁ' || code.reading === 'LÁ';
    if (
      isLa &&
      idx > 0 &&
      idx < codes.length - 1 &&
      codes[idx - 1].readingType === 'number' &&
      codes[idx + 1].readingType === 'number'
    ) {
      return { ...code, readingType: 'number' };
    }
    return code;
  });

export interface RowSigns {
  signs: SignCodeWithDiscourseUuid[];
  words: EditorWord[];
  errors: EditorMarkupError[];
  // Set when a sign lookup failed; the row must not validate until a retry
  // succeeds, or the word would be dropped from the saved text.
  lookupError?: Error;
}

const splitWords = (text: string) =>
  text.split(/[\s]+/).filter(word => word !== '');

// Restoring a draft mounts every row at once, and each row looks up all of
// its signs. Those background lookups go through a queue that lets only a
// few rows look up signs at a time, so a long text does not fire thousands
// of requests together. A row the user is typing in skips the queue, so it
// never waits behind a restore, and a queued lookup whose text the user has
// since changed is dropped before it takes a slot.
const MAX_ROWS_IN_FLIGHT = 4;
let rowsInFlight = 0;
const waitingRows: (() => void)[] = [];

const acquireRowSlot = async () => {
  if (rowsInFlight < MAX_ROWS_IN_FLIGHT) {
    rowsInFlight += 1;
    return;
  }
  // The releasing row hands its slot straight to this one.
  await new Promise<void>(resolve => {
    waitingRows.push(resolve);
  });
};

const releaseRowSlot = () => {
  const next = waitingRows.shift();
  if (next) {
    next();
  } else {
    rowsInFlight -= 1;
  }
};

export interface RowSignsOptions {
  // Restore and other non-typing lookups: queued behind the limit above.
  background?: boolean;
  // False once the row's text has changed; a queued lookup is then skipped.
  isCurrent?: () => boolean;
  // How long a queued lookup may hold its slot (tests shorten it).
  timeoutMs?: number;
}

// A queued lookup that has not settled by then gives its slot back and
// marks the row with a lookup error, so editing the line retries it.
const BACKGROUND_LOOKUP_TIMEOUT_MS = 30000;

const withTimeout = (
  lookup: Promise<RowSigns>,
  timeoutMs: number
): Promise<RowSigns> => {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<RowSigns>(resolve => {
    timer = setTimeout(
      () =>
        resolve({
          signs: [],
          words: [],
          errors: [],
          lookupError: new Error('The sign lookup timed out.'),
        }),
      timeoutMs
    );
  });
  return Promise.race([lookup, timeout]).finally(() => {
    if (timer !== undefined) {
      clearTimeout(timer);
    }
  });
};

/**
 * Turns one editor line into signs, words and markup errors. Returns null
 * for a background lookup that was superseded while it waited.
 */
export const getRowSigns = async (
  rowText: string,
  lookup: SignLookup,
  {
    background = false,
    isCurrent = () => true,
    timeoutMs = BACKGROUND_LOOKUP_TIMEOUT_MS,
  }: RowSignsOptions = {}
): Promise<RowSigns | null> => {
  if (!background) {
    return lookUpRowSigns(rowText, lookup);
  }
  await acquireRowSlot();
  try {
    if (!isCurrent()) {
      return null;
    }
    return await withTimeout(lookUpRowSigns(rowText, lookup), timeoutMs);
  } finally {
    releaseRowSlot();
  }
};

const lookUpRowSigns = async (
  rowText: string,
  lookup: SignLookup
): Promise<RowSigns> => {
  const { markupText, parseText } = convertDeterminatives(rowText);
  const markupRowText = markupText.replace(/\.\.\./g, '@');
  const parseRowText = parseText.replace(/\.\.\./g, '@');

  const errors: EditorMarkupError[] = [
    ...(await getMarkupInputErrors(markupRowText)),
  ];
  const fullMarkup = await applyMarkup(markupRowText);

  const parseWords = splitWords(parseRowText);
  let lookupError: Error | undefined;

  const piecesByWord: SignPiece[][] = await Promise.all(
    splitWords(markupRowText).map(async (word, wordIndex) => {
      try {
        const originalMarkup: EditorMarkup[] = fullMarkup.filter(
          markup => markup.wordIndex === wordIndex
        );
        const contextErrors = await getMarkupContextErrors(
          originalMarkup,
          word
        );
        errors.push(...contextErrors);

        const parsedSigns = parseWord(parseWords[wordIndex] || '')
          .map(sign => ({
            ...sign,
            clean: sign.text.replace(MARKUP_CHARS_PATTERN, ''),
          }))
          .filter(sign => sign.clean !== '');

        const formattedSigns = await Promise.all(
          parsedSigns.map(sign => lookup.getFormattedSign(sign.clean))
        );
        const readings = formattedSigns.flatMap((pieces, signIdx) =>
          pieces.map((reading, pieceIdx) => ({
            reading,
            markup: originalMarkup[signIdx],
            isDeterminative: parsedSigns[signIdx].isDeterminative,
            divider:
              pieceIdx < pieces.length - 1 ? '+' : parsedSigns[signIdx].divider,
          }))
        );

        const codes = markSubtractiveNumbers(
          await Promise.all(
            readings.map(piece =>
              lookup.getSignCode(
                piece.reading,
                piece.isDeterminative ? 'isPercent' : 'notPercent'
              )
            )
          )
        );

        parsedSigns.forEach((sign, signIdx) => {
          const signStart = formattedSigns
            .slice(0, signIdx)
            .reduce((sum, pieces) => sum + pieces.length, 0);
          const signCodes = codes.slice(
            signStart,
            signStart + formattedSigns[signIdx].length
          );
          if (
            /^\d{3,}$/.test(sign.clean) &&
            signCodes.some(code => !code.type)
          ) {
            errors.push({
              error: 'Write hundreds as me-at, not as digits:',
              text: sign.clean,
            });
          }
        });

        return codes.map((code, idx) => {
          const { divider } = readings[idx];
          const visibleDivider = /[%¤¦]/.test(divider)
            ? spellingSeparator(divider) || '%'
            : divider;
          return {
            code: {
              ...code,
              uuid: v4(),
              post: visibleDivider || undefined,
              reading: readings[idx].reading,
              markup: readings[idx].markup,
            },
            isDeterminative: readings[idx].isDeterminative,
            divider,
          };
        });
      } catch (err) {
        lookupError = err as Error;
        return [];
      }
    })
  );

  const words: EditorWord[] = piecesByWord.map(pieces => {
    const spelling = buildSpelling(pieces);
    return {
      discourseUuid: spelling !== '|' ? v4() : null,
      spelling,
    };
  });

  const signs: SignCodeWithDiscourseUuid[] = piecesByWord.flatMap(
    (pieces, idx) =>
      pieces.map(piece => ({
        ...piece.code,
        discourseUuid: words[idx].discourseUuid,
      }))
  );

  return { signs, words, errors, lookupError };
};
